import { useQuery } from "@apollo/client";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { gql } from "generated-graphql";
import { useDebounce } from "hooks/useDebounce";
import { useMemo, useState } from "react";

export const SEARCH_TENANTS_QUERY = gql(`
  query DispatchPipelineSearchTenants($search: String, $pageSize: Int, $sort: [SortModel!]) {
    tenants(search: $search, pageSize: $pageSize, sort: $sort) {
      items {
        id
        name
      }
      count
    }
  }
`);

export type TenantOption = { id: string; name: string };

export const APPROVED_GROUP = "Approved";
export const OTHER_GROUP = "Other organizations";

type GroupedTenantOption = TenantOption & { group: string };

interface DispatchOrganizationPickerProps {
  approvedTenants: TenantOption[];
  /**
   * Organizations with a pipeline built for them for this vendor. Offered even
   * when unapproved so their pipeline can run for QC; for a vendor with no
   * base pipeline these are the only organizations that can run at all.
   */
  builtForTenants: TenantOption[];
  /** When false, only the approved and built-for organizations are offered — no search. */
  hasBasePipeline: boolean;
  value: TenantOption | null;
  onChange: (value: TenantOption | null) => void;
  helperText?: React.ReactNode;
  error?: boolean;
  disabled?: boolean;
}

/**
 * Organization picker for a chosen vendor: the vendor's approved organizations
 * first, then the unapproved organizations it was built for, then (when the
 * vendor has a base pipeline) any other organization found by search. Anything
 * unapproved can run the pipeline for QC only.
 */
export function DispatchOrganizationPicker({
  approvedTenants,
  builtForTenants,
  hasBasePipeline,
  value,
  onChange,
  helperText,
  error,
  disabled,
}: DispatchOrganizationPickerProps) {
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 300);

  // Only active, non-demo organizations with the Waste module can be QC
  // targets: the Vendor Data tab that approves the pair lists exactly those
  // organizations, so a run for anything else could never be approved.
  const scopedSearch =
    `hasFlag:${FeatureFlagKey.WasteModule} status:active ${debouncedSearch}`.trim();

  const { data, loading } = useQuery(SEARCH_TENANTS_QUERY, {
    variables: {
      search: scopedSearch,
      pageSize: 25,
      sort: [{ field: "name", sort: "asc" as const }],
    },
    skip: disabled || !hasBasePipeline,
  });

  // The selected organization stays in the option list even after a new search
  // drops it from the results, so the field keeps showing the selection.
  const { options, selected } = useMemo(() => {
    const approvedIds = new Set(approvedTenants.map((tenant) => tenant.id));
    const approved = approvedTenants.map((tenant) => ({ ...tenant, group: APPROVED_GROUP }));
    const builtFor = builtForTenants
      .filter((tenant) => !approvedIds.has(tenant.id))
      .map((tenant) => ({ ...tenant, group: OTHER_GROUP }));
    const listedIds = new Set([...approvedIds, ...builtFor.map((tenant) => tenant.id)]);
    const others: GroupedTenantOption[] = (data?.tenants.items ?? [])
      .filter((tenant) => !listedIds.has(tenant.id))
      .map((tenant) => ({ id: tenant.id, name: tenant.name, group: OTHER_GROUP }));
    const listed = [...approved, ...builtFor, ...others];
    if (value === null) return { options: listed, selected: null };
    const found = listed.find((option) => option.id === value.id);
    if (found) return { options: listed, selected: found };
    const kept: GroupedTenantOption = { ...value, group: OTHER_GROUP };
    return { options: [...listed, kept], selected: kept };
  }, [approvedTenants, builtForTenants, data, value]);

  return (
    <Autocomplete
      options={options}
      value={selected}
      disabled={disabled}
      groupBy={(option) => option.group}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, other) => option.id === other.id}
      onChange={(_, option) => onChange(option ? { id: option.id, name: option.name } : null)}
      onInputChange={(_, input) => setSearch(input)}
      loading={loading}
      loadingText="Loading..."
      noOptionsText="No organizations found."
      renderInput={(params) => (
        <TextField
          {...params}
          label="Organization"
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
